/**
 * AIService.js
 * Service for AI operations - integrates with backend OpenAI implementation
 */

import { getBackendUrl } from '../config/environments';

/**
 * Generates an image using OpenAI DALL-E 3 via backend
 * @param {string} prompt - The text prompt for image generation
 * @param {string} sizeConfig - The size configuration from constants (e.g., '1024x1024', '1024x1024_hd')
 * @param {string} walletAddress - User's wallet address for authentication
 * @param {object} options - Additional options (style, creativity, etc.)
 * @returns {Promise<object>} Generated image data
 */
export async function generateImage(prompt, sizeConfig = '1024x1024', walletAddress, options = {}) {
  try {
    if (!prompt || !walletAddress) {
      throw new Error('Prompt and wallet address are required');
    }

    // Get backend URL from environment configuration
    const backendUrl = getBackendUrl();
    
    // Parse size configuration to get OpenAI size and quality
    let openaiSize = '1024x1024';
    let quality = 'standard';
    
    if (sizeConfig.includes('_hd')) {
      quality = 'hd';
      openaiSize = sizeConfig.replace('_hd', '');
    } else {
      openaiSize = sizeConfig;
    }
    
    // Enhance prompt ONLY if explicitly requested
    let enhancedPrompt = prompt;
    if (options.enhancePrompt === true) {
      enhancedPrompt = enhancePromptForDallE(prompt, options.stylePreset);
    }
    
    console.log(`🎨 Requesting image generation: "${enhancedPrompt}" (${openaiSize}, ${quality})`);
    console.log(`📝 Enhancement ${options.enhancePrompt ? 'ENABLED' : 'DISABLED'} - Original: "${prompt}"`);
    if (options.enhancePrompt && enhancedPrompt !== prompt) {
      console.log(`✨ Enhanced prompt: "${enhancedPrompt}"`);
    }
    
    // Prepare request body
    const requestBody = {
      prompt: enhancedPrompt,
      size: openaiSize,
      quality: quality,
      walletAddress: walletAddress
    };
    
    // Call backend API
    const response = await fetch(`${backendUrl}/api/ai/generate-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Image generation failed');
    }
    
    console.log(`✅ Image generated successfully`);
    
    return {
      imageUrl: data.imageUrl,
      originalPrompt: prompt,
      enhancedPrompt: enhancedPrompt,
      revisedPrompt: data.revisedPrompt, // DALL-E 3 sometimes revises prompts
      parameters: data.parameters,
      timestamp: new Date().toISOString()
    };
    
  } catch (error) {
    console.error('Error in generateImage:', error);
    throw error;
  }
}

/**
 * Enhances a prompt for better DALL-E 3 results
 * ONLY ADDS technical and quality terms - does NOT change core content
 * @param {string} prompt - Original prompt
 * @param {string} stylePreset - Style preset from UI
 * @returns {string} Enhanced prompt
 */
function enhancePromptForDallE(prompt, stylePreset) {
  if (!prompt) return prompt;
  
  let enhancedPrompt = prompt.trim();
  
  // Add style-specific technical enhancements (ONLY technical terms)
  const styleEnhancements = {
    'photorealistic': ', photorealistic, high resolution',
    'digital-art': ', digital art style, crisp details',
    'anime': ', anime art style, clean lines',
    '3d-render': ', 3D rendered, smooth surfaces',
    'oil-painting': ', oil painting technique, textured brushstrokes',
    'concept-art': ', concept art style, professional illustration',
    'pixel-art': ', pixel art style, sharp pixels',
    'cinematic': ', cinematic composition, professional lighting'
  };
  
  if (stylePreset && styleEnhancements[stylePreset]) {
    enhancedPrompt += styleEnhancements[stylePreset];
  }
  
  // Only add general quality terms if not already present
  if (!enhancedPrompt.toLowerCase().includes('detailed') && 
      !enhancedPrompt.toLowerCase().includes('quality') &&
      !enhancedPrompt.toLowerCase().includes('resolution')) {
    enhancedPrompt += ', high quality';
  }
  
  return enhancedPrompt;
}

/**
 * Sends a message to AI chat (placeholder for future OpenAI chat integration)
 * @param {string} message - User message
 * @param {string} walletAddress - User's wallet address
 * @param {Array} conversationHistory - Previous messages for context
 * @returns {Promise<object>} AI response
 */
export async function sendChatMessage(message, walletAddress, sessionId = null) {
  try {
    if (!message || !walletAddress) {
      throw new Error('Message and wallet address are required');
    }

    const backendUrl = getBackendUrl();
    
    console.log(`💬 Sending chat message from ${walletAddress}`);
    
    const body = {
      walletAddress: walletAddress,
      message: message
    };
    
    // Add sessionId only if provided (for continuing existing chats)
    if (sessionId) {
      body.sessionId = sessionId;
    }
    
    console.debug('[chat-body]', body);
    
    const response = await fetch(`${backendUrl}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.debug('[chat-response]', data);
    
    if (!data.success) {
      throw new Error(data.error || 'Chat request failed');
    }
    
    return {
      response: data.reply || data.response || 'No response received',
      model: data.model,
      sessionId: data.sessionId,
      timestamp: new Date().toISOString()
    };
    
  } catch (error) {
    console.error('Error in sendChatMessage:', error);
    throw error;
  }
}

/**
 * Validates if the prompt is appropriate for image generation
 * @param {string} prompt - The prompt to validate
 * @returns {object} Validation result
 */
export function validatePrompt(prompt) {
  if (!prompt || prompt.trim().length === 0) {
    return {
      valid: false,
      error: 'Prompt cannot be empty'
    };
  }
  
  if (prompt.length < 3) {
    return {
      valid: false,
      error: 'Prompt is too short (minimum 3 characters)'
    };
  }
  
  if (prompt.length > 1000) {
    return {
      valid: false,
      error: 'Prompt is too long (maximum 1000 characters)'
    };
  }
  
  // Check for potentially inappropriate content (basic filter)
  const inappropriateWords = ['explicit', 'nsfw', 'nude', 'sexual'];
  const lowerPrompt = prompt.toLowerCase();
  
  for (const word of inappropriateWords) {
    if (lowerPrompt.includes(word)) {
      return {
        valid: false,
        error: 'Prompt contains inappropriate content'
      };
    }
  }
  
  return {
    valid: true,
    enhancedPrompt: prompt.trim()
  };
}

/**
 * Generates a video using Runway ML via backend
 * @param {object} params - video generation parameters
 * @param {string} params.promptText - Text prompt for the video
 * @param {string} params.promptImage - Optional image URL or data URI
 * @param {string} params.ratio - Aspect ratio (e.g., '1280:720')
 * @param {number} params.duration - Duration in seconds
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<object>} Generated video data
 */
export async function generateVideo({ promptText, promptImage = '', ratio = '512:512', duration = 5 }, walletAddress) {
  try {
    if (!promptText || !walletAddress) {
      throw new Error('Prompt text and wallet address are required');
    }

    const backendUrl = getBackendUrl();

    const response = await fetch(`${backendUrl}/api/ai/generate-video`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ promptText, promptImage, ratio, duration, walletAddress })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Video generation failed');
    }

    return {
      videoUrl: data.videoUrl,
      taskId: data.taskId,
      originalPrompt: promptText,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error in generateVideo:', error);
    throw error;
  }
} 